import React, { useState, useEffect } from "react";
import { ArrowRight, ChevronLeft, Info, Phone, X } from "lucide-react";
import { toast } from "react-toastify";
import axios from "../../axios";
import { FaWhatsapp } from "react-icons/fa";

const OTPModal = ({
  phone,
  onVerify,
  onClose,
  onChangeNumber,
  onResend,
  value,
  onValueChange,
  error,
  resendTimer: externalResendTimer,
  canResend: externalCanResend,
  isVerifying: verifyingFromParent,
  isResending: resendingFromParent,
}) => {
  const [internalOtp, setInternalOTP] = useState("");
  const [resendTimer, setResendTimer] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const otp = value ?? internalOtp;
  const visibleTimer = externalResendTimer ?? resendTimer;
  const canResend = externalCanResend ?? visibleTimer <= 0;
  const verifying = verifyingFromParent ?? isVerifying;
  const resending = resendingFromParent ?? isResending;
  const displayPhone = phone?.startsWith("+") ? phone : `+91 ${phone || ""}`;

  useEffect(() => {
    if (externalResendTimer !== undefined) return;
    if (resendTimer <= 0) return;
    const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(timer);
  }, [externalResendTimer, resendTimer]);

  const updateOtp = (nextOtp) => {
    if (onValueChange) {
      onValueChange(nextOtp);
    } else {
      setInternalOTP(nextOtp);
    }
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;

    const digits = value.slice(-4).split("");
    const otpArr = otp.padEnd(4, " ").split("");

    if (digits.length > 1) {
      digits.forEach((digit, digitIndex) => {
        if (index + digitIndex < 4) otpArr[index + digitIndex] = digit;
      });
    } else {
      otpArr[index] = value;
    }

    const nextOtp = otpArr.join("").replace(/\s/g, "").slice(0, 4);
    updateOtp(nextOtp);

    const nextIndex = Math.min(index + Math.max(digits.length, 1), 3);
    if (value && index < 3) {
      document.getElementById(`otp-modal-${nextIndex}`)?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key !== "Backspace") return;

    const otpArr = otp.padEnd(4, " ").split("");
    if (!otpArr[index]?.trim() && index > 0) {
      otpArr[index - 1] = " ";
      document.getElementById(`otp-modal-${index - 1}`)?.focus();
    } else {
      otpArr[index] = " ";
    }
    updateOtp(otpArr.join("").replace(/\s/g, ""));
  };

  const handleVerify = async () => {
    if (otp.length !== 4) {
      toast.error("Please enter a valid 4-digit OTP");
      return;
    }
    try {
      setIsVerifying(true);
      await onVerify(otp);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    try {
      setIsResending(true);
      if (onResend) {
        await onResend();
      } else {
        await axios.post("/generate-otp", { phoneNumber: phone });
        toast.success(`OTP sent to ${displayPhone}`);
      }
      setResendTimer(60);
    } catch (err) {
      console.error(err);
      toast.error("Failed to resend OTP");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-[#fbfbfc] text-left text-[#242329] md:flex md:h-screen md:items-center md:justify-center md:overflow-hidden md:bg-[rgba(25,20,40,0.45)] md:px-6 md:backdrop-blur-[3px]">
      <div className="relative w-full max-w-md overflow-hidden px-4 pb-8 pt-5 text-left md:max-w-[520px] md:rounded-[32px] md:border md:border-white/70 md:bg-white md:px-8 md:py-8 md:shadow-[0_30px_80px_rgba(34,30,38,0.22)]">
        <div className="absolute -right-36 -top-40 h-[430px] w-[430px] rounded-full bg-[#f2dff4] md:-right-32 md:-top-48 md:h-[430px] md:w-[430px]" />

        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 z-20 hidden h-11 w-11 items-center justify-center rounded-full border border-[#eee7f6] bg-white text-[#4a245a] shadow-[0_10px_20px_rgba(34,30,38,0.10)] md:flex"
          aria-label="Close popup"
        >
          <X size={22} strokeWidth={2.2} />
        </button>

        <button
          type="button"
          onClick={onClose}
          className="relative z-10 flex h-10 w-10 items-center justify-center rounded-full bg-[#f3f3f3] text-black md:hidden md:h-12 md:w-12"
          aria-label="Go back"
        >
          <ChevronLeft className="md:h-8 md:w-8" size={24} strokeWidth={2} />
        </button>

        <div className="relative z-10 mt-10 inline-flex items-start justify-start gap-2 rounded-full bg-[#dffbea] px-4 py-2 text-sm font-medium text-[#176b61] shadow-sm md:mt-0 md:py-1.5 md:text-xs">
          <FaWhatsapp size={20} className="text-[#20c36b]" />
          WhatsApp Verification
        </div>

        <h2 className="relative z-10 mt-2 text-[24px] font-bold leading-tight text-[#242329] md:mt-4 md:text-[28px]">
          Enter the 4-digit code
        </h2>
        <p className="relative z-10 mt-1 text-[14px] text-[#7b7a80] md:mt-2 md:text-sm md:leading-6">
          We sent an OTP to your WhatsApp number. Enter it below to continue
          securely.
        </p>

        <div className="relative z-10 mt-4 inline-flex items-center gap-3 rounded-2xl bg-[#efedf0] px-4 py-3 text-sm font-medium text-[#27252b] md:mt-4 md:py-2 md:text-base">
          <Phone size={21} className="text-[#642568]" />
          {displayPhone}
        </div>

        <div className="relative z-10 mt-4 rounded-[28px] border border-[#f1eef3] bg-white px-4 py-4 shadow-[0_18px_28px_rgba(34,30,38,0.18)] md:mt-5 md:px-5 md:py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-[#242329] md:text-xl">
                Verify OTP
              </h3>
              <p className="text-sm text-[#8b8990] md:text-sm">
                Paste or type your code
              </p>
            </div>
            <button
              type="button"
              onClick={onChangeNumber || onClose}
              className="rounded-2xl bg-[#f5edf9] px-5 py-2 text-sm font-semibold text-[#482053] md:px-4 md:py-2"
            >
              Edit Number
            </button>
          </div>

          <div className="mt-4 grid grid-cols-4 gap-2 md:mt-5">
            {[0, 1, 2, 3].map((i) => (
              <input
                key={i}
                id={`otp-modal-${i}`}
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={otp[i] || ""}
                autoFocus={i === 0}
                onChange={(e) => handleOtpChange(e.target.value, i)}
                onKeyDown={(e) => handleOtpKeyDown(e, i)}
                className=" min-h-[6px] w-full rounded-2xl border-2 border-[#e3ddea] bg-[#fbf9fd] text-center text-4xl font-bold text-[#2d2b31] outline-none transition focus:border-[#6a2a73] focus:ring-4 focus:ring-[#f3e8f8] md:min-h-[58px] md:text-2xl"
              />
            ))}
          </div>

          {error && (
            <p className="mt-2 text-left text-sm font-medium text-red-600">
              {error}
            </p>
          )}

          <div className="mt-4 flex items-start gap-2 text-xs text-[#7f7d88] md:mt-4 md:text-xs md:leading-5">
            <Info size={18} className="shrink-0" />
            <span>If you copied the full OTP, you can paste it into any box.</span>
          </div>

          <div className="mt-4 flex justify-center md:mt-5 md:justify-center">
            {canResend ? (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="rounded-full bg-[#f7f0fb] px-4 py-2 text-base font-semibold text-[#5a2764] disabled:opacity-60 md:px-7 md:py-2.5 md:text-sm"
              >
                {resending ? "Resending..." : "Resend OTP"}
              </button>
            ) : (
              <div className="rounded-full bg-[#f7f0fb] px-4 py-2 text-base text-[#807b8a] md:px-7 md:py-2.5 md:text-sm">
                Resend available in{" "}
                <span className="font-extrabold text-[#402046]">
                  {visibleTimer}s
                </span>
              </div>
            )}
          </div>

          <button
            onClick={handleVerify}
            disabled={verifying}
            className={`mt-6 flex h-16 w-full items-center justify-center gap-4 rounded-2xl text-lg font-bold text-white shadow-[0_12px_20px_rgba(85,37,96,0.28)] md:mt-5 md:h-12 md:text-lg ${
              verifying ? "bg-gray-400 cursor-not-allowed" : "bg-[#542461]"
            }`}
          >
            {verifying ? "Verifying..." : "Verify OTP"}
            {!verifying && <ArrowRight size={28} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPModal;
